/**
 * Digital Signature - Captures user's typed signature for legal agreements
 *
 * Features:
 * - Typed name input with validation
 * - SHA-256 hash generation
 * - Timestamp capture
 * - Audit trail metadata (for future backend)
 */

import { useState, useCallback } from 'react';
import { PenLine, Check, AlertCircle } from 'lucide-react';
import { generateSignatureHash } from '../../stores/onboardingStore';
import type { SignatureData } from '../../types/onboarding';
import { cn } from '../../lib/utils';

interface DigitalSignatureProps {
  onSign: (signature: SignatureData) => void;
  documentTitle: string;
  documentVersion: string;
  isComplete?: boolean;
  existingSignature?: SignatureData | null;
}

export default function DigitalSignature({
  onSign,
  documentTitle,
  documentVersion,
  isComplete = false,
  existingSignature,
}: DigitalSignatureProps) {
  const [typedName, setTypedName] = useState('');
  const [isAgreed, setIsAgreed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate the typed name
  const isValidName = typedName.trim().length >= 2 && typedName.includes(' ');

  const handleSign = useCallback(async () => {
    if (!isValidName || !isAgreed) {
      setError('Please enter your full legal name and agree to the terms.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const timestamp = new Date().toISOString();
      const hash = await generateSignatureHash(typedName.trim(), timestamp);

      const signature: SignatureData = {
        typedName: typedName.trim(),
        hash,
        signedAt: timestamp,
        // Note: IP and userAgent would be captured by backend
      };

      onSign(signature);
    } catch (err) {
      setError('Failed to generate signature. Please try again.');
      console.error('Signature error:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [typedName, isAgreed, isValidName, onSign]);

  // If already signed, show the existing signature
  if (existingSignature) {
    return (
      <div
        className="p-6 rounded-xl"
        style={{
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid var(--color-success)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="p-2 rounded-full"
            style={{ backgroundColor: 'var(--color-success)' }}
          >
            <Check className="w-5 h-5" style={{ color: 'white' }} />
          </div>
          <div className="flex-1">
            <h4
              className="font-semibold mb-1"
              style={{ color: 'var(--color-success)' }}
            >
              Document Signed
            </h4>
            <p
              className="text-sm mb-3"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              You signed "{documentTitle}" (v{documentVersion})
            </p>

            {/* Signature Details */}
            <div
              className="p-4 rounded-lg space-y-2 text-sm"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-text-muted)' }}>Signed by:</span>
                <span
                  className="font-medium"
                  style={{
                    color: 'var(--color-text-primary)',
                    fontFamily: "'Brush Script MT', cursive",
                  }}
                >
                  {existingSignature.typedName}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-text-muted)' }}>Date:</span>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {new Date(existingSignature.signedAt).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--color-text-muted)' }}>Signature ID:</span>
                <span
                  className="font-mono text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {existingSignature.hash.substring(0, 16)}...
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-6 rounded-xl"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div
          className="p-2 rounded-lg"
          style={{ backgroundColor: 'var(--color-accent-muted)' }}
        >
          <PenLine className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
        </div>
        <div>
          <h4
            className="font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Digital Signature Required
          </h4>
          <p
            className="text-sm"
            style={{ color: 'var(--color-text-muted)' }}
          >
            For "{documentTitle}" (v{documentVersion})
          </p>
        </div>
      </div>

      {/* Name Input */}
      <div className="mb-4">
        <label
          htmlFor="signature-name"
          className="block text-sm font-medium mb-2"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Type your full legal name
        </label>
        <input
          id="signature-name"
          type="text"
          value={typedName}
          onChange={(e) => {
            setTypedName(e.target.value);
            setError(null);
          }}
          placeholder="John Doe"
          disabled={isComplete}
          className={cn(
            'w-full px-4 py-3 rounded-lg text-lg transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-2'
          )}
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
            fontFamily: "'Brush Script MT', cursive",
            // @ts-expect-error CSS custom properties for ring
            '--tw-ring-color': 'var(--color-accent)',
            '--tw-ring-offset-color': 'var(--color-bg)',
          }}
          aria-describedby="signature-help"
        />
        <p
          id="signature-help"
          className="mt-1 text-xs"
          style={{ color: 'var(--color-text-muted)' }}
        >
          Enter your first and last name as it appears on official documents
        </p>
      </div>

      {/* Agreement Checkbox */}
      <label
        className={cn(
          'flex items-start gap-3 p-4 rounded-lg cursor-pointer mb-4 transition-colors'
        )}
        style={{
          backgroundColor: isAgreed
            ? 'var(--color-accent-muted)'
            : 'var(--color-bg-tertiary)',
        }}
      >
        <input
          type="checkbox"
          checked={isAgreed}
          onChange={(e) => {
            setIsAgreed(e.target.checked);
            setError(null);
          }}
          disabled={isComplete}
          className="mt-0.5 w-4 h-4 rounded"
          style={{ accentColor: 'var(--color-accent)' }}
        />
        <span
          className="text-sm leading-relaxed"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          I hereby acknowledge that by typing my name above, I am creating a
          legally binding digital signature. I have read, understood, and agree
          to be bound by the terms of this document.
        </span>
      </label>

      {/* Error Message */}
      {error && (
        <div
          className="flex items-center gap-2 p-3 rounded-lg mb-4"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: 'var(--color-error)',
          }}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Sign Button */}
      <button
        onClick={handleSign}
        disabled={!isValidName || !isAgreed || isProcessing || isComplete}
        className={cn(
          'w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl',
          'font-semibold transition-all duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'hover:scale-[1.02] active:scale-100'
        )}
        style={{
          backgroundColor:
            isValidName && isAgreed
              ? 'var(--color-accent)'
              : 'var(--color-bg-tertiary)',
          color:
            isValidName && isAgreed
              ? 'var(--color-bg)'
              : 'var(--color-text-muted)',
        }}
      >
        {isProcessing ? (
          <>
            <div
              className="w-4 h-4 border-2 rounded-full animate-spin"
              style={{
                borderColor: 'var(--color-bg)',
                borderTopColor: 'transparent',
              }}
            />
            Processing...
          </>
        ) : (
          <>
            <PenLine className="w-4 h-4" />
            Sign Document
          </>
        )}
      </button>

      {/* Legal Notice */}
      <p
        className="mt-4 text-xs text-center"
        style={{ color: 'var(--color-text-muted)' }}
      >
        Your signature will be timestamped and a cryptographic hash will be
        generated for verification purposes.
      </p>
    </div>
  );
}
